import { Badge } from "../badge";
import { AVATAR_INITIALEN, initialen } from "../formular/auswahl-konfiguration";
import { breiteKlasse, cx } from "../fortschritt/breite";
import { Zustandspille } from "../zustandspille";
import { formatDatum, formatZahl, type DatumArt } from "./tabellen-logik";

/*
 * Zellentyp-Katalog (T18, `tabellen.md`), Grundsatz Teil 1: Person, Status, Fortschritt, Datum, Zahl.
 * Rein darstellend (kein Zustand). Teil 2: Tags, Aktionen (über `TabellenZeile.aktionen`), KI, Herkunft.
 */

/** Person: Initialen im Kreis + Name; nie E-Mail als Haupttext, keine Aktivitäts-/Leistungsdaten (r177/r179). */
export function PersonZelle({ name, zusatz }: { name: string; zusatz?: string }) {
  return (
    <>
      {/* Preline: data-display/data-display-sub-tables-records/data-display-cat-user-and-contact-tables/member-table-with-status-badges */}
      <span className="flex items-center gap-x-2.5 min-w-0">
        <span className={AVATAR_INITIALEN} aria-hidden="true">
          {initialen(name)}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm text-foreground">{name}</span>
          {zusatz && <span className="block truncate text-xs text-muted-foreground-1">{zusatz}</span>}
        </span>
      </span>
    </>
  );
}

export type ZellenStatus = "ok" | "inArbeit" | "geplant" | "blockiert" | "ueberfaellig";

/**
 * Status-Pille nach M-001: Erledigt `teal`, In Arbeit `primary`, Geplant gedämpft, Blockiert/Überfällig rot mit Punkt
 * (`Zustandspille`). Das Zustandswort steht immer als Text; höchstens eine Pille je Achse.
 */
export function StatusZelle({ status, children }: { status: ZellenStatus; children: React.ReactNode }) {
  switch (status) {
    case "ok":
      return <Badge ton="erfolg">{children}</Badge>;
    case "inArbeit":
      return <Badge ton="primaer">{children}</Badge>;
    case "geplant":
      return <Badge ton="gedaempft">{children}</Badge>;
    case "blockiert":
      return (
        <Zustandspille ton="gefahr" mitPunkt>
          {children}
        </Zustandspille>
      );
    case "ueberfaellig":
      return <Zustandspille ton="ueberfaellig">{children}</Zustandspille>;
  }
}

const BALKEN_FUELLUNG = "flex flex-col justify-center rounded-full overflow-hidden bg-primary text-xs text-primary-foreground text-center whitespace-nowrap transition duration-500";

/** Fortschritt: Zahl („3/5“ oder „60 %“) + Balken `w-16 h-1.5`, Breite per Klasse (nie `style=`). */
export function FortschrittZelle({ prozent, text, label }: { prozent: number; text?: string; label?: string }) {
  const p = Math.min(100, Math.max(0, Math.round(prozent)));
  return (
    <>
      {/* Preline: data-display/data-display-sub-tables-records/data-display-cat-project-tables/project-table-with-search-and-filters (progressbar) */}
      <span className="flex items-center gap-x-2">
        <span className="text-sm text-foreground tabular-nums whitespace-nowrap">{text ?? `${p} %`}</span>
        <span
          className="flex w-16 h-1.5 bg-surface-1 rounded-full overflow-hidden"
          role="progressbar"
          aria-label={label ?? "Fortschritt"}
          aria-valuenow={p}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span className={cx(BALKEN_FUELLUNG, breiteKlasse(p))} />
        </span>
      </span>
    </>
  );
}

/**
 * Datum (T14): `<time datetime>`, Fristen absolut „TT.MM.JJJJ“, Ereignisse bis 7 Tage relativ; Vollwert als `title`.
 * Überfällig = Pille daneben (Aufrufer), das Datum bleibt grau.
 */
export function DatumZelle({ wert, art, jetzt }: { wert: string | Date; art: DatumArt; jetzt?: Date }) {
  const d = formatDatum(wert, art, jetzt);
  if (!d.datetime) return <span className="text-sm text-muted-foreground-1">{d.text}</span>;
  return (
    <time className="text-sm text-muted-foreground-1 whitespace-nowrap" dateTime={d.datetime} title={d.voll}>
      {d.text}
    </time>
  );
}

/**
 * Wert mit Beleg (T18, Analytics Lauf-Detail, Betreiber 22.10.2026 „Empfehlung“ T4 A): der Wert steht oben, das Belegzitat direkt
 * darunter eingerückt mit Linie, kursiv und in Anführungszeichen (Zitatform nach `review-moderation-table`, ohne Bilder).
 * Rein darstellend. Ein Feld ohne Zitat gibt es nicht: unbelegte Felder erscheinen nie als Wert, nur als Zähler (Aufrufer).
 */
export function WertMitBeleg({ wert, zitat }: { wert: string; zitat: string }) {
  return (
    <>
      {/* Preline: data-display/data-display-sub-tables-records/data-display-cat-content-tables/review-moderation-table (Zitatzeile unter „You replied with“) */}
      <span className="block min-w-0">
        <span className="block text-sm text-foreground break-words">{wert}</span>
        <span className="mt-1 ms-3 ps-3 block border-s-2 border-line-2 text-xs italic text-muted-foreground-1 break-words">„{zitat}“</span>
      </span>
    </>
  );
}

/**
 * KI-Zelle (T18, Zellentyp „KI“): Lucide `Sparkles` plus Wort, `ai-gated ai-mark` (R-D). Die Zelle zeigt, dass der Inhalt der Zeile
 * von der KI stammt; sie ist kein Auslöser. Das Wort trägt die Aussage, das Symbol ist Beiwerk (`aria-hidden`).
 */
export function KiZelle({ children }: { children: React.ReactNode }) {
  return (
    <>
      {/* Preline: data-display/data-display-sub-tables-records/data-display-cat-user-and-contact-tables/member-table-with-status-badges (Textzelle mit Symbol) */}
      <span className="ai-gated ai-mark inline-flex items-center gap-x-1.5 text-sm text-muted-foreground-1">
        <svg className="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
          <path d="M20 3v4" />
          <path d="M22 5h-4" />
          <path d="M4 17v2" />
          <path d="M5 18H3" />
        </svg>
        {children}
      </span>
    </>
  );
}

/** Zahl/Währung (T8): rechtsbündig, `tabular-nums`, `de-DE`, Minus U+2212, Einheit in der Zelle, keine Farbe. */
export function ZahlZelle({ wert, nachkomma, einheit }: { wert: number; nachkomma?: number; einheit?: string }) {
  return <span className="block text-end tabular-nums">{formatZahl(wert, { nachkomma, einheit })}</span>;
}
